import React from "react";

type UnsavedChangesContextValue = {
  hasUnsavedChanges: boolean;
  setHasUnsavedChanges: (value: boolean) => void;
  setDiscardDraftHandler: (handler: (() => Promise<void>) | null) => void;
  discardDraft: () => Promise<void>;
};

const UnsavedChangesContext = React.createContext<UnsavedChangesContextValue | null>(null);

export const UnsavedChangesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false);
  const discardDraftHandlerRef = React.useRef<(() => Promise<void>) | null>(null);

  const setDiscardDraftHandler = React.useCallback((handler: (() => Promise<void>) | null) => {
    discardDraftHandlerRef.current = handler;
  }, []);

  const discardDraft = React.useCallback(async () => {
    if (!discardDraftHandlerRef.current) return;
    await discardDraftHandlerRef.current();
  }, []);

  return (
    <UnsavedChangesContext.Provider
      value={{
        hasUnsavedChanges,
        setHasUnsavedChanges,
        setDiscardDraftHandler,
        discardDraft,
      }}
    >
      {children}
    </UnsavedChangesContext.Provider>
  );
};

export function useUnsavedChanges() {
  const ctx = React.useContext(UnsavedChangesContext);
  if (!ctx) {
    throw new Error("useUnsavedChanges must be used inside UnsavedChangesProvider");
  }
  return ctx;
}

